import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { Loader2, Mail } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { TOAST_CONFIG } from '@/lib/constants'
import { useSubmitContactRequest } from '@/hooks/misc'

const contactFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  workEmail: z.string().email('Please enter a valid email'),
  phoneNumber: z
    .string()
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  message: z
    .string()
    .min(10, 'Please provide more details (minimum 10 characters)'),
})

type ContactFormData = z.output<typeof contactFormSchema>
type ContactFormInput = z.input<typeof contactFormSchema>

interface ContactFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ContactFormModal = ({
  open,
  onOpenChange,
}: ContactFormModalProps) => {
  const { requestData, loading } = useSubmitContactRequest()
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<ContactFormInput, unknown, ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      workEmail: '',
      phoneNumber: '',
      message: '',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = form

  const onSubmit = async (data: ContactFormData) => {
    try {
      await requestData({
        firstName: data.firstName,
        lastName: data.lastName,
        workEmail: data.workEmail,
        phoneNumber: data.phoneNumber || undefined,
        message: data.message,
      })

      setSubmitted(true)
      reset()
    } catch (error) {
      toast.error('Failed to send message. Please try again.', TOAST_CONFIG)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(() => {
      setSubmitted(false)
      reset()
    }, 200)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-white">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-2xl font-bold text-neutral-900 text-center">
                Message Sent!
              </DialogTitle>
              <DialogDescription className="text-neutral-600 text-center">
                Thanks for reaching out. We&apos;ll get back to you within one
                business day.
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={handleClose}
              className={cn(
                'mt-6',
                'bg-orange-primary hover:bg-orange-hover',
                'text-white font-semibold',
                'px-8 py-2.5 rounded-full'
              )}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-neutral-900">
                Get in Touch
              </DialogTitle>
              <DialogDescription className="text-neutral-600">
                Have a question? We&apos;d love to hear from you.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="firstName"
                    className="text-sm font-medium text-neutral-700"
                  >
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    {...register('firstName')}
                    placeholder="John"
                    autoComplete="off"
                    disabled={loading}
                    className={cn(
                      'border-neutral-300 focus:border-orange-primary focus:ring-orange-primary',
                      errors.firstName &&
                        'border-red-500 focus:border-red-500 focus:ring-red-500'
                    )}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-500">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="lastName"
                    className="text-sm font-medium text-neutral-700"
                  >
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    {...register('lastName')}
                    placeholder="Doe"
                    autoComplete="off"
                    disabled={loading}
                    className={cn(
                      'border-neutral-300 focus:border-orange-primary focus:ring-orange-primary',
                      errors.lastName &&
                        'border-red-500 focus:border-red-500 focus:ring-red-500'
                    )}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-500">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="workEmail"
                  className="text-sm font-medium text-neutral-700"
                >
                  Work Email
                </Label>
                <Input
                  id="workEmail"
                  type="email"
                  {...register('workEmail')}
                  placeholder="john@company.com"
                  disabled={loading}
                  className={cn(
                    'border-neutral-300 focus:border-orange-primary focus:ring-orange-primary',
                    errors.workEmail &&
                      'border-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                />
                {errors.workEmail && (
                  <p className="text-sm text-red-500">
                    {errors.workEmail.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="phoneNumber"
                  className="text-sm font-medium text-neutral-700"
                >
                  Phone Number{' '}
                  <span className="text-neutral-400">(optional)</span>
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  {...register('phoneNumber')}
                  placeholder="(555) 123-4567"
                  disabled={loading}
                  className="border-neutral-300 focus:border-orange-primary focus:ring-orange-primary"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-sm font-medium text-neutral-700"
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  {...register('message')}
                  placeholder="How can we help you?"
                  rows={4}
                  disabled={loading}
                  className={cn(
                    'border-neutral-300 resize-none focus:border-orange-primary focus:ring-orange-primary focus-visible:outline-none focus-visible:ring-0',
                    errors.message &&
                      'border-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                />
                {errors.message && (
                  <p className="text-sm text-red-500">
                    {errors.message.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={loading}
                  className="text-neutral-600 hover:text-neutral-900"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  className={cn(
                    'bg-orange-primary hover:bg-orange-hover',
                    'text-white font-semibold',
                    'px-6 rounded-full',
                    'transition-all duration-200'
                  )}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
